/*
 * LSR MODIFICATION (2026-09-10) — new file, not a stock Horizon asset.
 *
 * WHY THIS EXISTS
 * The HoneyBear fundraiser product (honeybear-lyneham-primary-fundraiser)
 * collects two required line item properties before it can be packed and
 * credited: "Teacher's name" and "Student's name". They are two stock
 * product-custom-property blocks in templates/product.honeybear.json
 * (block ids lsr_teacher_name and lsr_student_name), both required, both
 * max length 45, and both emitted by blocks/product-custom-property.liquid
 * as text inputs named properties[Teacher's name] and
 * properties[Student's name].
 *
 * THE GAP IS VALIDATION, NOT DATA. Shopify's dynamic/accelerated checkout
 * buttons — Shop Pay and the express wallet buttons — DO carry line item
 * properties through to the order exactly as the ordinary Add to Cart path
 * does. The only gap is that they submit the product form without running
 * its HTML validation, so both names can arrive blank. When that happens
 * the school cannot be told which class and which child to credit, which
 * is the entire point of the product.
 *
 * WHAT IT DOES
 * While either name is empty or whitespace-only, the accelerated checkout
 * container is made genuinely non-interactive — pointer-events: none,
 * reduced opacity, aria-disabled="true" — and a short line of text below
 * it explains why. The element is never removed from the DOM, so nothing
 * reflows when it re-enables. The moment both fields have content the
 * container returns to normal.
 *
 * The ordinary Add to Cart button is deliberately untouched. It already
 * validates correctly through the browser's own required-field handling.
 *
 * SELECTORS, AND WHY THESE ONES
 * Horizon renders the express buttons in blocks/accelerated-checkout.liquid
 * as <div class="accelerated-checkout-block"> wrapping the output of
 * {{ form | payment_button }}, which is Shopify's own
 * <div class="shopify-payment-button">. The theme's own wrapper is
 * preferred because it is present in the server-rendered HTML, whereas
 * Shopify injects the actual wallet buttons into .shopify-payment-button
 * asynchronously. .shopify-payment-button is kept as a fallback in case
 * the theme wrapper is ever renamed upstream.
 *
 * The two inputs are found by the properties[ name prefix combined with
 * the required attribute rather than by their literal property keys. The
 * keys contain a typographic apostrophe (U+2019), and matching on it is
 * needlessly brittle; the prefix test selects exactly these two fields on
 * this page, and would pick up a third required property automatically.
 *
 * LATE RENDER
 * The wallet buttons arrive after first paint. A MutationObserver on the
 * document watches for the container appearing or being replaced, backed
 * by a bounded poll (400ms, giving up after 24s) for the case where the
 * injection happens without a mutation the observer sees. Every path runs
 * the same idempotent apply() — re-running it is harmless.
 *
 * SCOPE / REVERT
 * Loaded by one handle-gated line in snippets/stylesheets.liquid, matching
 * the gating pattern already used there for lsr-pattern.css. Delete that
 * line to revert. This file is inert if it ever loads elsewhere: with no
 * required properties[...] fields on the page it disables nothing.
 */

(function () {
  'use strict';

  var MESSAGE_ID = 'lsr-honeybear-express-note';
  var MESSAGE_TEXT = "Add the teacher's and student's names to use express checkout.";
  var FLAG = 'lsrHoneybearGuardActive';
  var POLL_INTERVAL_MS = 400;
  var POLL_MAX_TICKS = 60;

  if (window[FLAG]) return;
  window[FLAG] = true;

  function requiredPropertyFields() {
    return Array.prototype.slice.call(
      document.querySelectorAll(
        'input[name^="properties["][required], textarea[name^="properties["][required]'
      )
    );
  }

  function expressContainers() {
    var found = Array.prototype.slice.call(
      document.querySelectorAll('.accelerated-checkout-block')
    );

    if (found.length === 0) {
      found = Array.prototype.slice.call(
        document.querySelectorAll('.shopify-payment-button')
      );
    }

    return found;
  }

  function allNamesFilled(fields) {
    if (fields.length === 0) return true;

    for (var i = 0; i < fields.length; i++) {
      if (String(fields[i].value || '').trim() === '') return false;
    }

    return true;
  }

  function noteFor(container) {
    var next = container.nextElementSibling;

    if (next && next.id === MESSAGE_ID) return next;

    var note = document.createElement('p');
    note.id = MESSAGE_ID;
    note.textContent = MESSAGE_TEXT;
    note.setAttribute('data-lsr-honeybear-note', 'true');
    note.style.margin = '8px 0 0';
    note.style.fontSize = '0.875rem';
    note.style.lineHeight = '1.4';
    note.style.color = 'var(--color-foreground, #3A3A3C)';

    container.insertAdjacentElement('afterend', note);

    return note;
  }

  function disable(container) {
    if (container.getAttribute('data-lsr-express-state') !== 'disabled') {
      container.style.pointerEvents = 'none';
      container.style.opacity = '0.45';
      container.setAttribute('aria-disabled', 'true');
      container.setAttribute('data-lsr-express-state', 'disabled');
    }

    var note = noteFor(container);
    note.hidden = false;
    container.setAttribute('aria-describedby', MESSAGE_ID);
  }

  function enable(container) {
    if (container.getAttribute('data-lsr-express-state') !== 'enabled') {
      container.style.pointerEvents = '';
      container.style.opacity = '';
      container.removeAttribute('aria-disabled');
      container.setAttribute('data-lsr-express-state', 'enabled');
    }

    var next = container.nextElementSibling;
    if (next && next.id === MESSAGE_ID) next.hidden = true;
    container.removeAttribute('aria-describedby');
  }

  function apply() {
    var fields = requiredPropertyFields();

    if (fields.length === 0) return;

    var containers = expressContainers();
    var filled = allNamesFilled(fields);

    for (var i = 0; i < containers.length; i++) {
      if (filled) {
        enable(containers[i]);
      } else {
        disable(containers[i]);
      }
    }

    bindFields(fields);
  }

  function bindFields(fields) {
    for (var i = 0; i < fields.length; i++) {
      if (fields[i].getAttribute('data-lsr-guard-bound') === 'true') continue;

      fields[i].setAttribute('data-lsr-guard-bound', 'true');
      fields[i].addEventListener('input', apply);
      fields[i].addEventListener('change', apply);
      fields[i].addEventListener('blur', apply);
    }
  }

  var scheduled = false;

  function schedule() {
    if (scheduled) return;

    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      apply();
    });
  }

  function start() {
    apply();

    if (typeof window.MutationObserver === 'function') {
      new window.MutationObserver(schedule).observe(document.documentElement, {
        childList: true,
        subtree: true
      });
    }

    var ticks = 0;
    var poll = window.setInterval(function () {
      ticks++;
      apply();
      if (ticks >= POLL_MAX_TICKS) window.clearInterval(poll);
    }, POLL_INTERVAL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
